import { useState, useMemo, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import PageContainer from '../pageContainer';
import AvatarUploadZone from '../avatarUploadZone';
import ShareTypeBox from '../shareTypeBox';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ArrowTooltip from '../arrowTooltip';
import { ReactComponent as LogoSlack } from '../../../assets/icons/slack-no-text.svg';
import { sticky } from '../../../constants/helperCssRules';
import { SYSTEM_ROLES } from '../../../constants/roles';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { personSelector } from '../../../store/selectors/personSelector';
import { updateCurrentUserInfo } from '../../../store/modules/auth';
import {
  clearPerson,
  getPerson,
  updatePersonPhoto,
} from '../../../store/modules/person';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import { isObjectEmpty } from '../../../utility/helpers';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';
import { getPersonFullName } from '../../../utility/uiUtils';
import { systemRoleCheck } from '../../../utility/roles';
import { getReportingRelationshipLabel, getNavItems } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    position: 'relative',
  },
  pageTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: spacing(5),
    ...sticky(primary.white),
  },
  stickyHorizontalNavigation: {
    position: 'sticky',
    zIndex: 999,
    background: primary.white,
    top: 84,
    paddingBottom: spacing(6),
  },
  avatar: {
    marginRight: spacing(4),
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 600,
  },
  rightSide: {
    display: 'flex',
    alignSelf: 'flex-end',
  },
  shareType: {
    flexShrink: 0,
    marginLeft: spacing(4),
  },
  slackIcon: {
    width: 48,
    height: 48,
  },
}));

const PEOPLE_PROFILE_PATH = /^\/people\/(\d+)(?:\/|$)/;

const shouldClearPersonOnLeave = () =>
  !PEOPLE_PROFILE_PATH.test(window.location.pathname);

const PeoplePageContainer = ({
  translations,
  location,
  auth,
  accessControl,
  userId,
  children,
  shouldPassProps = true,
  navigate,
  dispatch,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const { VisitEMP } = EVENT_ACTION_TYPES;

  const user = useSelector(personSelector);

  const [isLoading, setIsLoading] = useState(true);

  const isUserLoaded = !isObjectEmpty(user);
  const navigationItems = useMemo(
    () =>
      getNavItems(
        translations.pageContainer,
        auth,
        accessControl,
        userId,
        organizationSettings
      ),
    [translations, auth, accessControl, userId, organizationSettings]
  );

  useEffect(() => {
    let isCancelled = false;
    const isSamePersonLoaded =
      user?.id != null && Number(user.id) === Number(userId);

    if (isSamePersonLoaded) {
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    getPerson(dispatch, userId).then(({ data }) => {
      if (isCancelled) return;

      const isAssociate = systemRoleCheck(data, SYSTEM_ROLES.ASSOCIATE);

      if (isAssociate) {
        navigate('/not-found', { replace: true });
        return;
      }

      setIsLoading(false);
      tagManagerDataLayer(
        VisitEMP.action,
        VisitEMP.name,
        location.pathname.replace(userId, ':id')
      );
    });

    return () => {
      isCancelled = true;
    };
  }, [
    userId,
    user?.id,
    dispatch,
    navigate,
    location.pathname,
    VisitEMP.action,
    VisitEMP.name,
  ]);

  useEffect(
    () => () => {
      if (shouldClearPersonOnLeave()) {
        dispatch(clearPerson());
      }
    },
    [dispatch]
  );

  const onSuccessUpload = files => {
    const formData = new FormData();
    formData.append('avatar', files[0]);

    return updatePersonPhoto(dispatch, user.id, formData).then(userData => {
      if (user.id === auth.id)
        dispatch(updateCurrentUserInfo({ avatar: userData.avatar }));
    });
  };

  const renderPageHeader = () => {
    const canChangeAvatar = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PROFILES_EDIT,
      accessControl,
      auth.id,
      user.id
    );

    const reportingRelationshipLabel = getReportingRelationshipLabel(
      translations.pageContainer,
      auth,
      accessControl,
      userId
    );

    return (
      <div className={classes.headerTitle}>
        <div className={classes.avatar}>
          <AvatarUploadZone
            translations={translations.pageContainer.avatarUpload}
            user={user}
            disabled={!canChangeAvatar}
            onSuccessHandler={onSuccessUpload}
          />
        </div>
        <TextBoxWithTooltip variant="h1" text={getPersonFullName(user)} />
        {user?.slack_id && (
          <ArrowTooltip
            position="right"
            tooltipLabel={translations.pageContainer.slackTooltip}
          >
            <LogoSlack className={classes.slackIcon} />
          </ArrowTooltip>
        )}
        {reportingRelationshipLabel && (
          <ShareTypeBox
            className={classes.shareType}
            label={reportingRelationshipLabel}
          />
        )}
      </div>
    );
  };

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      location={location}
      navigationItems={navigationItems}
      user={user}
      headerProps={{
        renderTitle: isUserLoaded ? renderPageHeader : undefined,
      }}
      isLoading={!isUserLoaded}
      shouldPassProps={shouldPassProps}
      navigate={navigate}
      dispatch={dispatch}
      organizationSettings={organizationSettings}
    >
      {!isLoading && isUserLoaded && user.id === userId ? (
        children
      ) : (
        <Fragment />
      )}
    </PageContainer>
  );
};

PeoplePageContainer.propTypes = {
  translations: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  shouldPassProps: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  organizationSettings: PropTypes.object.isRequired,
};

export default PeoplePageContainer;
